import { ReactNode } from 'react';
import Btn from './ui/Btn';

/**
 * Carte d'un outil IA dans la vue d'accueil "Outils IA" (ADR-025).
 *
 * Pattern extensible : chaque futur scan IA (synthèse, suggestions
 * d'auditions à demander, etc.) ajoutera une instance de cette carte
 * dans `<OutilsIaHome />`, avec son icône Tabler et sa description.
 *
 * États :
 *   - Outil sans cycle scan/résultats (Recherche) : un seul bouton
 *     "Ouvrir".
 *   - Outil avec scan : bouton primaire selon l'état du dernier scan :
 *       - jamais scanné → "Scanner le dossier"
 *       - scan existe → "Voir les résultats" + bouton secondaire
 *         "Re-scanner" à côté.
 *     L'état du dernier scan est affiché en sous-ligne (date + nb
 *     résultats).
 */

export type AiToolCardProps = {
  icon?: ReactNode;
  title: string;
  description: string;
  /** Si présent, état du dernier scan ; sinon, jamais scanné. */
  lastScan?: { at: string | null; count: number } | null;
  /** Outil sans cycle scan (Recherche) — bouton "Ouvrir" simple. */
  onOpen?: () => void;
  /** Outil avec scan — bouton "Scanner le dossier" (1er run) ou
   *  "Re-scanner" (run suivant). */
  onScan?: () => void;
  /** Outil avec scan — bouton "Voir les résultats" si déjà scanné. */
  onViewResults?: () => void;
};

export default function AiToolCard({
  icon,
  title,
  description,
  lastScan,
  onOpen,
  onScan,
  onViewResults,
}: AiToolCardProps) {
  const hasScan = !!lastScan && lastScan.at !== null;

  return (
    <article className="rounded-md border border-rule bg-paper-elevated p-4">
      <header className="flex items-start gap-2.5">
        {icon && (
          <span className="mt-0.5 text-[20px] leading-none text-text-secondary">{icon}</span>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-[14px] font-medium text-text-primary">{title}</h3>
          <p className="mt-1 text-[12px] leading-relaxed text-text-secondary">{description}</p>
        </div>
      </header>

      {lastScan !== undefined && (
        <p className="mt-3 text-[11px] text-text-tertiary">
          {hasScan && lastScan.at ? (
            <>
              Dernier scan :{' '}
              <span className="text-text-secondary">
                {new Date(lastScan.at).toLocaleDateString('fr-BE', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
              {' · '}
              <span className="font-medium text-text-primary">
                {lastScan.count} résultat{lastScan.count > 1 ? 's' : ''}
              </span>
            </>
          ) : (
            <span className="italic">Jamais scanné</span>
          )}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {onOpen && (
          <Btn variant="primary" size="sm" onClick={onOpen}>
            Ouvrir
          </Btn>
        )}
        {onViewResults && hasScan && (
          <Btn variant="primary" size="sm" onClick={onViewResults}>
            Voir les résultats
          </Btn>
        )}
        {onScan && (
          <Btn variant={hasScan ? 'secondary' : 'primary'} size="sm" onClick={onScan}>
            {hasScan ? 'Re-scanner' : 'Scanner le dossier'}
          </Btn>
        )}
      </div>
    </article>
  );
}
